import React, { useEffect, useState, useContext } from 'react'
import { Outlet } from 'react-router'
import { useParams } from 'react-router'
import TitleContext from '../../../TitleContext'
import EmptyPage from '../../Elements/EmptyPage'
import Typography from '@mui/material/Typography'
import LoadingProgress from '../../Elements/LoadingProgress'
import { events as eventApi } from '../../../api/swagger'
import { Event } from '../../../swagger'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import useLoadingContext from '../../../hooks/useLoadingContext'
import { OperationIds } from '../../../swagger/operationIdEnum'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'
import { useTranslation } from 'react-i18next'
import { useTheme, useMediaQuery } from '@mui/material'
import { Tab } from '@mui/material'
import { TabContext, TabList } from '@mui/lab'

const InternalEventDetails: React.FC = () => {
  const { t } = useTranslation()
  const { useTitleEffect } = useContext(TitleContext)
  const { handleError, handleMessage } = useAppSnackbar()
  useTitleEffect(t('Events.EventDetails.Header', 'Leadership Training Events'))
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { eventKey } = useParams<{ eventKey: string }>()
  const eventId = parseInt(`${eventKey}`)
  const { Events } = useLoadingIds()

  const fetchEventLoadingId = Events[OperationIds.FetchEvent]

  const [activeTab] = useState('Details')
  const [isRegistered, setIsRegistered] = useState(false)
  const [eventDetails, setEventDetails] = useState<Event | undefined>(undefined)
  const [isRegistering, setIsRegistering] = useState(false)
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false)

  const fetchEvent = async () => {
    try {
      const fetched = await eventApi.fetchEvent({ eventKey: eventId })
      setEventDetails(fetched)
      setIsRegistered(Boolean(fetched?.isRegistered))
    } catch (error) {
      setEventDetails(undefined)

      await handleError(
        error,
        t(
          'LeadershipTrainingEvents.Fetch.Error',
          'An unknown error occurred fetching leadership training events.'
        )
      )
    } finally {
      setHasAttemptedFetch(true)
    }
  }

  const { triggerFetch: triggerFetchEvent, isLoading } = useLoadingContext({
    loadingId: fetchEventLoadingId,
    asyncFunction: fetchEvent,
  })

  useEffect(() => {
    if (!isNaN(eventId)) {
      setHasAttemptedFetch(false)
      triggerFetchEvent()
    }
  }, [eventId, triggerFetchEvent])

  const handleRegister = async () => {
    if (!eventDetails || isRegistering) return
    setIsRegistering(true)
    try {
      await eventApi.createLeadershipTrainingRegistrant({ eventKey: eventId })
      setIsRegistered(true)
      setEventDetails((prev) =>
        prev ? { ...prev, attendees: (prev.attendees ?? 0) + 1 } : prev
      )
      handleMessage(t('Events.Register.Success', "You're Registered!"))
    } catch (error) {
      await handleError(
        error,
        t(
          'Events.Register.Error',
          'An unknown error occurred while registering.'
        )
      )
    } finally {
      setIsRegistering(false)
    }
  }

  if (isNaN(eventId)) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'Events.EmptyState.Message',
              `The event either does not exist or you do not have permission to view it.`
            )}
          </Typography>
        }
      />
    )
  }

  if (isLoading || !hasAttemptedFetch) return <LoadingProgress />

  if (!eventDetails) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'Events.EmptyState.Message',
              `The event either does not exist or you do not have permission to view it.`
            )}
          </Typography>
        }
      />
    )
  }

  const canRegister =
    !!eventDetails.acceptingRegistrations && eventDetails.registrationOpen

  return (
    <TabContext value={activeTab}>
      <TabList
        value={activeTab}
        textColor="primary"
        variant="scrollable"
        scrollButtons={false}
      >
        <Tab
          key="Details"
          value="Details"
          label={t('EventDetails.TabTitle.Details', 'Details')}
        />
      </TabList>
      <Outlet
        context={{
          eventDetails,
          isMobile,
          canRegister,
          isRegistering,
          isRegistered,
          handleRegister,
        }}
      />
    </TabContext>
  )
}

export default InternalEventDetails
